/**
 * The clinic's check-in inbox — who has arrived, and how far they got.
 *
 * This page is the point of the feature. Until it existed a clinic could hang a
 * QR code on its wall and never see an answer: the intake was readable only by
 * the patient, and reached a clinician only if they scanned the patient's
 * 7-digit code within 24 hours. That handoff still exists and is still how the
 * answers reach a chart; this is the clinic seeing its own waiting room.
 *
 * Completed check-ins only. A half-answered intake is not something the desk can
 * act on: it has no reason-for-visit yet, and a patient who put their phone away
 * mid-question has not told the clinic anything. Partial sessions are still
 * written (they are what lets a patient carry on after a dropped connection), they
 * are simply not listed until they are finished.
 *
 * Live rather than fetched, and ordered by updatedAt, so a check-in appears the
 * moment the patient finishes it.
 *
 * Two states, not one. Until a clinic switches check-ins on there is nothing to
 * invite anyone to and nothing to list, so the page offers one thing: turning it
 * on. Showing an invite button and an empty table first meant every control on
 * the page did nothing useful yet, and a QR code that told whoever scanned it the
 * clinic was not using check-ins.
 */

import React, { useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { format, isToday } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { contentCardSx } from "styles/standaloneLayoutTokens";
import { useAuth } from "hooks/useAuth";
import { VisitStartCheckInsContext } from "context/VisitStartCheckIns";
import { saveVisitStartConfig } from "services/visitStartCheckInsService";
import { getVisitStartConfigurableElements } from "features/visitStart/visitStartElementsCatalog";
import VisitStartInviteDialog from "components/dashboard/visitStartCheckIns/VisitStartInviteDialog";
import VisitStartCheckInDetail from "components/dashboard/visitStartCheckIns/VisitStartCheckInDetail";
import {
  checkInChiefComplaint,
  checkInPatientLabel,
  checkInStatus,
} from "components/dashboard/visitStartCheckIns/visitStartCheckInSummary";

/**
 * What a clinic gets on the way in. Only the configurable ids are stored — the
 * core ones are re-added when a session resolves its question set.
 */
const DEFAULT_SELECTION = getVisitStartConfigurableElements()
  .filter((el) => el.defaultEnabled)
  .map((el) => el.elementId);

/**
 * Firestore hands back a Timestamp, but a row written locally seconds ago still
 * has its serverTimestamp pending and reads as null. Both are normal here — a
 * check-in being created is exactly what this page watches for.
 */
const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const timeLabel = (value) => {
  const date = toDate(value);
  if (!date) return "just now";
  return isToday(date) ? format(date, "HH:mm") : format(date, "d MMM, HH:mm");
};

const WHAT_THIS_IS =
  "Save time at the front desk by having patients answer your questions before they arrive, or while they wait. You choose which questions to ask. Their answers show up here.";

export default function VisitStartCheckInsPage({ data }) {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user } = useAuth();
  const {
    checkIns: rows,
    checkInsLoaded: loaded,
    checkInsError: error,
    config: profile,
    configLoaded,
  } = useContext(VisitStartCheckInsContext);
  const clinicId = data?.id ? String(data.id) : null;
  const clinicName = data?.title || "your clinic";

  const [inviteOpen, setInviteOpen] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState(null);

  const closeDetail = useCallback(
    () => navigate("/dashboard/visitstart"),
    [navigate],
  );

  /**
   * Turning it on keeps whatever question set the clinic already chose, so a
   * clinic that switched check-ins off in Settings and back on here does not
   * silently get the defaults back.
   */
  const handleEnable = useCallback(async () => {
    if (!clinicId) return;
    setEnabling(true);
    setEnableError(null);
    const enabledElements = Array.isArray(profile?.enabledElements)
      ? profile.enabledElements
      : DEFAULT_SELECTION;
    try {
      await saveVisitStartConfig(clinicId, {
        enabled: true,
        enabledElements,
        updatedBy: user?.uid || null,
      });
      // No optimistic state update: VisitStartCheckInsProvider's live
      // subscription is the source of truth and picks this write up on its own.
      // These two go in one commit so the off-panel releases with the success
      // alert already rendered, rather than a frame ahead of it.
      setJustEnabled(true);
      setEnabling(false);
    } catch (err) {
      console.error("[VisitStartCheckInsPage] enable failed:", err);
      setEnableError(err?.message || "Could not turn on check-ins.");
      setEnabling(false);
    }
  }, [clinicId, profile, user]);

  /**
   * `dismissedByClinic` is still filtered on even though nothing writes it any
   * more. The desk's "Remove from list" action is gone, but rows dismissed while
   * it existed are still flagged, and staff hid those deliberately — dropping the
   * check would make them all reappear. guestCleanup's sweep 3 still collects
   * them, so this drains on its own.
   */
  const completed = useMemo(
    () =>
      rows.filter(
        (row) => checkInStatus(row) === "completed" && !row.dismissedByClinic,
      ),
    [rows],
  );

  const enabled = Boolean(profile?.enabled);
  const waiting = !clinicId || !configLoaded;

  // Rendered from every branch below: a permalink to one check-in must open it
  // even while the clinic's profile is still loading, or check-ins are off —
  // a link shared before a clinic switched the feature off should not 404.
  const detailDialog = (
    <CheckInDetailDialog
      sessionId={sessionId}
      loaded={loaded}
      rows={rows}
      onClose={closeDetail}
    />
  );

  if (waiting) {
    return (
      <div className="inner">
        <DashboardPageHeader title="Check-ins" subtitle={WHAT_THIS_IS} />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
        {detailDialog}
      </div>
    );
  }

  // `enabling` holds this branch open while the write is in flight, which is not
  // the same condition as `!enabled`. The config write is a direct client write,
  // so the live subscription fires a LOCAL snapshot the moment it is queued and
  // flips `enabled` true well before the server acks. Keying the branch on
  // `enabled` alone therefore swapped in the inbox first and let the success
  // alert land a beat later, shoving the table down the page. Holding until the
  // write resolves means the inbox and its alert arrive in one commit.
  if (!enabled || enabling) {
    return (
      <div className="inner">
        <DashboardPageHeader title="Check-ins" subtitle={WHAT_THIS_IS} />
        <Paper variant="outlined" sx={{ ...contentCardSx, p: { xs: 3, sm: 5 } }}>
          <Typography variant="h6" gutterBottom>
            Check-ins are off
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: "48em" }}>
            Turn them on and you will get a QR code to print for your waiting
            room. Patients can answer without an account, and you can change which
            questions they are asked at any time.
          </Typography>
          {enableError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {enableError}
            </Alert>
          ) : null}
          <LoadingButton
            variant="contained"
            size="large"
            loading={enabling}
            onClick={handleEnable}
          >
            Turn on check-ins
          </LoadingButton>
        </Paper>
        {detailDialog}
      </div>
    );
  }

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Check-ins"
        subtitle={
          <Box>
            <Typography>{WHAT_THIS_IS}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Change which questions are asked in{" "}
              <Link component={RouterLink} to="/dashboard/settings">
                Settings
              </Link>
              .
            </Typography>
          </Box>
        }
        // Only once there is a list to sit above. On an empty inbox the empty
        // state already offers this, and two identical buttons on one screen read
        // as two different actions.
        actions={
          completed.length > 0 ? (
            <Button variant="contained" onClick={() => setInviteOpen(true)}>
              Invite patients
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {justEnabled ? (
        <Alert
          severity="success"
          onClose={() => setJustEnabled(false)}
          sx={{ mb: 3 }}
        >
          Check-ins are on. Click Invite patients to get the code for your
          waiting room.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ ...contentCardSx, overflow: "hidden" }}>
        {!loaded ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : completed.length === 0 ? (
          <EmptyState onInvite={() => setInviteOpen(true)} />
        ) : (
          <List disablePadding>
            {completed.map((row, index) => (
              <React.Fragment key={row.id}>
                {index > 0 ? <Divider component="li" /> : null}
                <CheckInRow
                  row={row}
                  onOpen={() => navigate(`/dashboard/visitstart/${row.id}`)}
                />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <VisitStartInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        clinicId={clinicId}
        clinicName={clinicName}
      />

      {detailDialog}
    </div>
  );
}

function CheckInDetailDialog({ sessionId, loaded, rows, onClose }) {
  const open = Boolean(sessionId);
  const session = open ? rows.find((row) => row.id === sessionId) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {session ? checkInPatientLabel(session) : "Check-in"}
      </DialogTitle>
      <DialogContent>
        {!loaded ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : session ? (
          <VisitStartCheckInDetail session={session} />
        ) : (
          <Alert severity="info">
            This check-in is no longer here. A patient's own unfinished check-in
            can expire before the clinic opens it.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

CheckInDetailDialog.propTypes = {
  sessionId: PropTypes.string,
  loaded: PropTypes.bool,
  rows: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};

function CheckInRow({ row, onOpen }) {
  const complaint = checkInChiefComplaint(row);

  return (
    <ListItemButton onClick={onOpen} sx={{ py: 1.5, px: 2 }}>
      <ListItemText
        disableTypography
        // No status: every row here is a finished check-in, so a "Completed" chip
        // on each of them says nothing the list does not already mean.
        primary={
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {checkInPatientLabel(row)}
          </Typography>
        }
        secondary={
          <Box sx={{ mt: 0.25 }}>
            {complaint ? (
              <Typography variant="body2" color="text.secondary">
                {complaint}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {timeLabel(row.updatedAt || row.createdAt)}
            </Typography>
          </Box>
        }
      />
      <ChevronRightRounded sx={{ fontSize: 18, color: "text.disabled", ml: 1 }} />
    </ListItemButton>
  );
}

CheckInRow.propTypes = {
  row: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
};

/** One state, since there is nothing to filter by any more. */
function EmptyState({ onInvite }) {
  return (
    <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
      <Typography sx={{ mb: 1, fontWeight: 600 }}>No check-ins yet</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Put your code up in the waiting room. Patients who scan it can answer your
        questions before they are called.
      </Typography>
      <Button variant="contained" onClick={onInvite}>
        Invite patients
      </Button>
    </Box>
  );
}

EmptyState.propTypes = {
  onInvite: PropTypes.func,
};

VisitStartCheckInsPage.propTypes = {
  /** The facility's `locations` doc, from LocationContext via DashboardRoutes. */
  data: PropTypes.object,
};
